/**
 * bestware-click-to-zoom.js
 *
 * Lets a shopper open the product gallery's fullscreen (PhotoSwipe) view by
 * clicking/tapping anywhere on the image, not just on the small zoom button.
 *
 * Non-invasive: it does NOT reimplement the lightbox. It forwards a genuine tap
 * to the theme's existing `.product-single__media-zoom` trigger, so all of the
 * theme's logic (item building, active index, PhotoSwipe options, the
 * `open-lightbox` body class, etc. in assets/product.js) runs unchanged.
 *
 * Tap vs. swipe: the gallery is a draggable Flickity carousel. On touch, a
 * horizontal swipe still synthesizes a `click`, so we can't act on `click`
 * alone. We measure pointer travel from `pointerdown`; if the pointer moved
 * more than MOVE_THRESHOLD it was a swipe/drag and we leave it to Flickity.
 *
 * Delivered as a standalone asset, included via a <script> tag in the custom
 * footer (sections/footer-bestware.liquid, product pages only); the gallery
 * template/section code is untouched. The related presentation (zoom cursor,
 * hiding the redundant zoom button, hiding PhotoSwipe's LQIP placeholder) lives
 * in the global stylesheet.
 */
(function () {
  'use strict';

  var MEDIA_SELECTOR = '.product-single__media-image';
  var ZOOM_SELECTOR = '.product-single__media-zoom';
  // Elements inside an image slide that must keep their own click behaviour.
  var INTERACTIVE_SELECTOR = 'a, button, video, model-viewer, .figcaption';
  // Pointer travel (px) beyond which the gesture is a swipe/drag, not a tap.
  var MOVE_THRESHOLD = 10;

  var startX = 0;
  var startY = 0;
  var moved = false;

  function movedFrom(x, y) {
    return Math.abs(x - startX) > MOVE_THRESHOLD || Math.abs(y - startY) > MOVE_THRESHOLD;
  }

  function onPointerDown(event) {
    // Only track gestures that begin on an image slide; otherwise mark as
    // "moved" so a stale value can never let an unrelated click through.
    if (!event.target.closest(MEDIA_SELECTOR)) {
      moved = true;
      return;
    }
    moved = false;
    startX = event.clientX;
    startY = event.clientY;
  }

  function onPointerMove(event) {
    if (!moved && movedFrom(event.clientX, event.clientY)) {
      moved = true;
    }
  }

  function markMoved() {
    moved = true;
  }

  // Capture phase so Flickity's own handlers can't hide these via stopPropagation.
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointercancel', markMoved, true);

  document.addEventListener('click', function (event) {
    var media = event.target.closest(MEDIA_SELECTOR);
    if (!media) return; // not an image slide (also excludes video / 3D-model slides)

    // The real zoom button already opens the lightbox — let it.
    if (event.target.closest(ZOOM_SELECTOR)) return;

    // Don't hijack clicks on other interactive bits (captions, links, etc.).
    if (event.target.closest(INTERACTIVE_SELECTOR)) return;

    // Swipe/drag rather than a tap? Leave it to the carousel.
    if (moved || movedFrom(event.clientX, event.clientY)) return;

    var zoom = media.querySelector(ZOOM_SELECTOR);
    if (!zoom) return; // lightbox disabled for this gallery (no trigger rendered)

    event.preventDefault();
    zoom.click(); // reuse the theme's PhotoSwipe click handler
  });
})();
